import { AlertTriangle } from 'lucide-react'

import { formatCurrency } from '@/lib/formatters'
import type { ParsedCustomerQuote } from '@/models/customerQuote'
import { CoveragesFieldset } from '@/routes/Private/App/Customers/Insurance/CoveragesFieldset'
import {
  computeTotalPremium,
  type CoverageFormData,
  type FeeFormData,
} from '@/routes/Private/App/Customers/Insurance/insuranceFormShared'
import { OneTimeFeesFieldset } from '@/routes/Private/App/Customers/Insurance/OneTimeFeesFieldset'

interface UploadQuoteReviewProps {
  parsed: ParsedCustomerQuote
  coverages: CoverageFormData[]
  onCoveragesChange: (next: CoverageFormData[]) => void
  fees: FeeFormData[]
  onFeesChange: (next: FeeFormData[]) => void
  /** Freezes the form once it's been submitted — what's in flight is what was sent. */
  disabled?: boolean
}

/**
 * What we read off the customer's quote, in the same editable fieldsets staff get on
 * the internal page — an LLM reading a PDF gets numbers wrong, and this is the only
 * chance to correct them before the quote is compared against real carrier prices.
 */
export function UploadQuoteReview({
  parsed,
  coverages,
  onCoveragesChange,
  fees,
  onFeesChange,
  disabled,
}: UploadQuoteReviewProps) {
  const total = computeTotalPremium(coverages, fees)

  return (
    <div className="max-h-[60vh] space-y-6 overflow-y-auto pr-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-foreground text-lg font-bold">{parsed.carrierName ?? 'Unnamed carrier'}</span>
        <span className="text-foreground text-lg font-extrabold">
          {formatCurrency(total)}
          <span className="text-ink-500 ml-1 text-sm font-normal">/ year</span>
        </span>
      </div>

      {parsed.warnings.length > 0 && (
        <div className="border-ink-100 flex gap-2 rounded-lg border p-3">
          <AlertTriangle className="text-ink-400 mt-0.5 size-4 shrink-0" />
          <ul className="text-ink-500 flex flex-col gap-1 text-xs">
            {parsed.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* A native fieldset, so every control below goes inert without threading a prop through both. */}
      <fieldset disabled={disabled} className="space-y-6">
        <CoveragesFieldset value={coverages} onChange={onCoveragesChange} showPremiumPerUnit={false} />
        <OneTimeFeesFieldset value={fees} onChange={onFeesChange} />
      </fieldset>
    </div>
  )
}
